import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  unminimizeCommentMutation,
  unminimizeCommentMutation$data,
  unminimizeCommentMutation$rawResponse,
} from './__generated__/unminimizeCommentMutation.graphql'

type UnminimizedComment =
  | NonNullable<unminimizeCommentMutation$data['unminimizeComment']>['unminimizedComment']
  | NonNullable<unminimizeCommentMutation$rawResponse['unminimizeComment']>['unminimizedComment']

function toObjectPropertiesPatch(comment: UnminimizedComment): ObjectPropertiesPatch | undefined {
  if (!comment || !('id' in comment) || !comment.id) return undefined

  const values: Record<string, unknown> = {
    isMinimized: comment.isMinimized,
    minimizedReason: comment.minimizedReason,
  }

  if ('pendingMinimizeReason' in comment) {
    values.pendingMinimizeReason = comment.pendingMinimizeReason
  }

  return {id: comment.id, values}
}

export function unminimizeCommentMutation({
  environment,
  input: {id},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {id: string}
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  const optimisticResponse: unminimizeCommentMutation$rawResponse = {
    unminimizeComment: {
      clientMutationId: '',
      unminimizedComment: {
        id,
        __typename: 'IssueComment',
        __isNode: 'IssueComment',
        isMinimized: false,
        minimizedReason: null,
        pendingMinimizeReason: null,
      },
    },
  }

  const optimisticPatch = toObjectPropertiesPatch(optimisticResponse.unminimizeComment?.unminimizedComment)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<unminimizeCommentMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation unminimizeCommentMutation($input: UnminimizeCommentInput!) @raw_response_type {
        unminimizeComment(input: $input) {
          clientMutationId
          unminimizedComment {
            isMinimized
            minimizedReason
            ... on IssueComment {
              id
              pendingMinimizeReason
            }
          }
        }
      }
    `,
    variables: {
      input: {subjectId: id},
    },
    optimisticResponse,
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError?.(error)
    },
    onCompleted: response => {
      if (response.unminimizeComment === null) {
        const error = new Error('unminimizeComment returned no payload')
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
        )
        onError?.(error)
      } else {
        const patch = toObjectPropertiesPatch(response.unminimizeComment?.unminimizedComment)
        if (patch) {
          dispatchMutationLifecycleEvent(
            new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
              phase: 'completed',
              mutationId,
              data: patch,
            }),
          )
        }
        onCompleted?.()
      }
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
